import { Box, Typography } from '@mui/material'
import { memo } from 'react'
import {
  FeeQuotedPrice,
  type FeeQuotedPriceControl,
} from '@screens/requests/components/PublisherRequest/approval/fee-quoted-price'
import type {
  TProcessingAction,
  TSong,
  TDraftReviewSong,
  TPublisherSong,
  TEnumItem,
} from '@modules/types'
import { SongHeader } from './SongHeader'

interface SongItemApprovalProps {
  song: TPublisherSong
  onApprove: (id: number) => void
  onReject: (id: number, reason: string) => void
  isLoading: boolean
  initialLoading: boolean
  processingSongId: number | null
  processingAction: TProcessingAction
  draftReviewSong: TDraftReviewSong
  distributionMethod: number
  enums: TEnumItem[]
  onChangeStatus: (songId: number) => void
  digitalCounterOfferFeeValidationError?: string
  printCounterOfferFeeValidationError?: string
  feeQuotedPriceControl: FeeQuotedPriceControl
}

const CONTAINER_STYLES = {
  bgcolor: 'background.default',
  mx: 2,
  gap: 2,
  pb: 4,
}

const CONTENT_STYLES = {
  p: 3,
  borderRadius: 1,
  bgcolor: 'background.default',
}

const SongComment = memo(({ comment }: { comment: string }) => (
  <>
    <Typography variant="caption" color="text.secondary" mb={0.5} mt={4}>
      Comment
    </Typography>
    <Typography variant="body1" color="text.secondary">
      {comment}
    </Typography>
  </>
))

export const SongItemApproval = memo(
  ({
    song,
    onApprove,
    onReject,
    isLoading,
    initialLoading,
    processingSongId,
    processingAction,
    draftReviewSong,
    distributionMethod,
    enums,
    onChangeStatus,
    digitalCounterOfferFeeValidationError,
    printCounterOfferFeeValidationError,
    feeQuotedPriceControl,
  }: SongItemApprovalProps) => {
    const isBlocked = Boolean(song.approvalDecision) || (isLoading && processingSongId === song.id)
    const isOtherSongProcessing =
      isLoading && processingSongId !== null && processingSongId !== song.id
    const isCounterOfferBlocked = isBlocked || isOtherSongProcessing
    const isActionButtonsBlocked = isBlocked || isOtherSongProcessing

    return (
      <Box>
        <Box sx={CONTAINER_STYLES}>
          <Box sx={CONTENT_STYLES}>
            <SongHeader
              song={song}
              onApprove={() => onApprove(song.id)}
              onReject={(songId, reason) => onReject(songId, reason)}
              processingSongId={processingSongId}
              processingAction={processingAction}
              draftReviewSong={draftReviewSong}
              onChangeStatus={onChangeStatus}
              isLoading={isLoading}
              initialLoading={initialLoading}
              isBlocked={isActionButtonsBlocked}
            />

            {song.additionalComments && <SongComment comment={song.additionalComments} />}
          </Box>

          <FeeQuotedPrice
            songHasStatus={Boolean(draftReviewSong?.songId && draftReviewSong?.isApproved !== null)}
            song={song as unknown as TSong}
            initialLoading={initialLoading}
            draftReviewSong={draftReviewSong}
            distributionMethod={distributionMethod}
            enums={enums}
            isBlocked={isCounterOfferBlocked}
            digitalCounterOfferFeeValidationError={digitalCounterOfferFeeValidationError}
            printCounterOfferFeeValidationError={printCounterOfferFeeValidationError}
            approvalDecision={song.approvalDecision}
            processingSongId={processingSongId}
            control={feeQuotedPriceControl}
          />
        </Box>
      </Box>
    )
  },
)
